import { AutoAwesome, Close, Psychology, Send, SmartToy } from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Collapse,
    Divider,
    IconButton,
    List,
    ListItem,
    Paper,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useChatAgent } from '@src/App/Components/Agents/hooks/useChatAgent';
import { EAgentModule, type IAgentContext, type IChatAgentMessage } from '@sharedInterfaces/ChatAgent';

export interface AgentAction {
    label: string;
    onClick: () => void;
    variant?: 'outlined' | 'text' | 'contained';
}

export interface AgentProps {
    message: string;
    context: 'onboarding' | 'employee-talk' | 'general';
    step?: number;
    onClose: () => void;
    actions?: AgentAction[];
    customActions?: React.ReactNode;
    enableChat?: boolean;
    onOpenMainChat?: () => void;
}

const CONTEXT_CONFIG = {
    onboarding: {
        name: 'T4LO',
        icons: [
            <Psychology key="psychology-0" />,
            <SmartToy key="smarttoy-1" />,
            <AutoAwesome key="autoawesome-2" />,
            <Psychology key="psychology-3" />,
            <AutoAwesome key="autoawesome-4" />,
        ],
    },
    'employee-talk': {
        name: 'Talk Assistant',
        icons: [<Psychology key="psychology-talk-0" />],
    },
    general: {
        name: 'AI Assistant',
        icons: [<SmartToy key="smarttoy-general-0" />],
    },
};

export default function MvpAgent({
    message,
    context,
    step = 0,
    onClose,
    actions = [],
    customActions,
    enableChat = true,
    onOpenMainChat,
}: AgentProps) {
    const theme = useTheme();
    const [currentMessage, setCurrentMessage] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [chatExpanded, setChatExpanded] = useState(false);
    const [userInput, setUserInput] = useState('');
    
    // Real chat integration
    const { messages: realMessages, sendMessage, isTyping: chatIsTyping } = useChatAgent({ 
        dummyMessage: message 
    });

    const config = CONTEXT_CONFIG[context];
    const currentIcon = config.icons[step] || config.icons[0];
    
    // Auto-expand chat when new messages arrive
    useEffect(() => {
        if (realMessages.length > 1) { // More than just dummy message
            setChatExpanded(true);
        }
    }, [realMessages.length]);

    useEffect(() => {
        setIsTyping(true);
        setCurrentMessage('');

        let index = 0;
        let animationId: number;

        const typeMessage = () => {
            if (index < message.length) {
                setCurrentMessage(message.slice(0, index + 1));
                index++;
                animationId = requestAnimationFrame(() => setTimeout(typeMessage, 50));
            } else {
                setIsTyping(false);
            }
        };

        const timer = setTimeout(typeMessage, 500);
        return () => {
            clearTimeout(timer);
            if (animationId) cancelAnimationFrame(animationId);
        };
    }, [message, step]);

    const handleSendMessage = async () => {
        if (!userInput.trim()) return;

        // Öffne Chat sofort beim Senden
        setChatExpanded(true);
        onOpenMainChat?.(); // Auch den Hauptchat öffnen falls OnboardingAgent

        const agentContext: IAgentContext = {
            module: context === 'onboarding' ? EAgentModule.ONBOARDING : EAgentModule.GENERAL,
            subContext: context === 'onboarding' ? {
                currentStep: step,
                stepName: getStepName(step)
            } : undefined
        };

        await sendMessage(userInput, agentContext);
        setUserInput('');
    };

    const getStepName = (currentStep: number): string => {
        const stepNames = ['Willkommen', 'CV-Upload', 'Analyse', 'Übersicht', 'Fertigstellung'];
        return stepNames[currentStep] || `Schritt ${currentStep + 1}`;
    };

    return (
        <Box sx={{ width: '100%', p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Avatar
                    sx={{
                        bgcolor: theme.palette.primary.main,
                        width: 40,
                        height: 40,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                    }}
                >
                    {currentIcon}
                </Avatar>

                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {config.name}
                        </Typography>
                        {isTyping && (
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: theme.palette.primary.main,
                                    animation: 'pulse 2s ease-in-out infinite',
                                    '@keyframes pulse': {
                                        '0%': { opacity: 1, transform: 'scale(1)' },
                                        '50%': { opacity: 0.6, transform: 'scale(1.1)' },
                                        '100%': { opacity: 1, transform: 'scale(1)' },
                                    },
                                }}
                            />
                        )}
                    </Box>

                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.palette.text.primary,
                            lineHeight: 1.6,
                            minHeight: '1.5em',
                        }}
                    >
                        {currentMessage}
                        {isTyping && (
                            <Box
                                component="span"
                                sx={{
                                    display: 'inline-block',
                                    width: 2,
                                    height: '1em',
                                    bgcolor: theme.palette.primary.main,
                                    ml: 0.5,
                                    animation: 'blink 1.2s ease-in-out infinite',
                                    '@keyframes blink': {
                                        '0%, 45%': { opacity: 1 },
                                        '50%, 95%': { opacity: 0.3 },
                                        '100%': { opacity: 1 },
                                    },
                                }}
                            />
                        )}
                    </Typography>
                </Box>

                <IconButton
                    size="small"
                    onClick={onClose}
                    sx={{
                        color: theme.palette.text.secondary,
                        '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                        },
                    }}
                >
                    <Close fontSize="small" />
                </IconButton>
            </Box>

            {/* Actions */}
            {!isTyping && (actions.length > 0 || customActions || enableChat) && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {actions.map((action, index) => (
                            <Button
                                key={index}
                                variant={action.variant || 'outlined'}
                                onClick={action.onClick}
                                size="small"
                                sx={{ textTransform: 'none' }}
                            >
                                {action.label}
                            </Button>
                        ))}

                        {customActions}

                        {enableChat && (
                            <Button
                                variant="text"
                                onClick={() => {
                                    setChatExpanded(!chatExpanded);
                                    // Auto-expand when clicking "Fragen?"
                                    if (!chatExpanded) {
                                        setChatExpanded(true);
                                    }
                                }}
                                size="small"
                                sx={{
                                    textTransform: 'none',
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                Fragen?
                            </Button>
                        )}
                    </Box>
                </>
            )}

            {/* Chat */}
            {enableChat && (
                <Collapse in={chatExpanded}>
                    <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 2 }} />

                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Hast du Fragen?"
                                value={userInput}
                                onChange={(e) => setUserInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                disabled={chatIsTyping}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    },
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleSendMessage}
                                disabled={!userInput.trim() || chatIsTyping}
                                sx={{
                                    height: '40px',
                                    minWidth: 40,
                                    borderRadius: 2,
                                    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                                }}
                            >
                                <Send fontSize="small" />
                            </Button>
                        </Box>

                        {/* Always show chat messages when expanded */}
                        <List sx={{ maxHeight: 200, overflow: 'auto', p: 0 }}>
                            {/* Show real chat messages (skip first dummy message) */}
                            {realMessages.slice(1).map((msg: IChatAgentMessage, index: number) => (
                                <ListItem
                                    key={`real-msg-${index}`}
                                    sx={{
                                        flexDirection: 'column',
                                        alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                        px: 0,
                                        py: 0.5,
                                    }}
                                >
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 1.5,
                                            maxWidth: '80%',
                                            bgcolor: msg.role === 'user'
                                                ? theme.palette.primary.main
                                                : alpha(theme.palette.grey[500], 0.1),
                                            color: msg.role === 'user' ? 'white' : theme.palette.text.primary,
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Typography variant="body2">{msg.text}</Typography>
                                    </Paper>
                                </ListItem>
                            ))}
                            
                            {chatIsTyping && (
                                <ListItem sx={{ px: 0, py: 0.5 }}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 1.5,
                                            bgcolor: alpha(theme.palette.grey[500], 0.1),
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            {config.name} tippt...
                                        </Typography>
                                    </Paper>
                                </ListItem>
                            )}
                            
                            {/* Show message when no messages yet */}
                            {realMessages.length <= 1 && !chatIsTyping && (
                                <ListItem sx={{ px: 0, py: 1, justifyContent: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Stelle deine erste Frage...
                                    </Typography>
                                </ListItem>
                            )}
                        </List>
                    </Box>
                </Collapse>
            )}
        </Box>
    );
}
